import React, { useState } from 'react';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import { cx } from './hooks';

/** Page numbers to show: first, last, and two either side of the current page. */
function pageList(current, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 2);
  const end = Math.min(totalPages - 1, current + 2);
  if (start > 2) pages.push('…prev');
  for (let p = start; p <= end; p += 1) pages.push(p);
  if (end < totalPages - 1) pages.push('…next');
  pages.push(totalPages);
  return pages;
}

/**
 * Controlled with `current` + `onChange`, or uncontrolled with `defaultCurrent`.
 * onChange(page, pageSize) fires for page and page-size changes.
 */
const Pagination = ({
  current,
  defaultCurrent = 1,
  total = 0,
  pageSize,
  defaultPageSize = 10,
  onChange,
  onShowSizeChange,
  showSizeChanger = false,
  pageSizeOptions = [10, 20, 50, 100],
  showQuickJumper = false,
  showTotal,
  hideOnSinglePage = false,
  align,
  className,
}) => {
  const [innerPage, setInnerPage] = useState(defaultCurrent);
  const [innerSize, setInnerSize] = useState(defaultPageSize);
  const [jump, setJump] = useState('');

  const size = pageSize ?? innerSize;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const page = Math.min(current ?? innerPage, totalPages);

  if (hideOnSinglePage && totalPages <= 1) return null;

  const goTo = p => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (current === undefined) setInnerPage(next);
    onChange?.(next, size);
  };

  const changeSize = newSize => {
    const nextPage = Math.min(page, Math.max(1, Math.ceil(total / newSize)));
    if (pageSize === undefined) setInnerSize(newSize);
    if (current === undefined) setInnerPage(nextPage);
    onShowSizeChange?.(nextPage, newSize);
    onChange?.(nextPage, newSize);
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * size + 1;
  const rangeEnd = Math.min(page * size, total);

  return (
    <nav className={cx('pagination', align === 'center' && 'is-center', className)} aria-label="Pagination">
      {showTotal && <span className="pagination-total">{showTotal(total, [rangeStart, rangeEnd])}</span>}

      <button
        type="button"
        className="page-btn"
        onClick={() => goTo(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <AiOutlineLeft />
      </button>

      {pageList(page, totalPages).map(p =>
        typeof p === 'string' ? (
          <button
            key={p}
            type="button"
            className="page-btn page-ellipsis"
            onClick={() => goTo(p === '…prev' ? page - 5 : page + 5)}
            aria-label={p === '…prev' ? 'Previous 5 pages' : 'Next 5 pages'}
          >
            •••
          </button>
        ) : (
          <button
            key={p}
            type="button"
            className={cx('page-btn', p === page && 'is-active')}
            onClick={() => goTo(p)}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        type="button"
        className="page-btn"
        onClick={() => goTo(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
      >
        <AiOutlineRight />
      </button>

      {showSizeChanger && (
        <select value={size} onChange={e => changeSize(Number(e.target.value))} aria-label="Items per page">
          {[...new Set([...pageSizeOptions.map(Number), size])].sort((a, b) => a - b).map(opt => (
            <option key={opt} value={Number(opt)}>
              {opt} / page
            </option>
          ))}
        </select>
      )}

      {showQuickJumper && (
        <label className="pagination-jump">
          Go to
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jump}
            onChange={e => setJump(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && jump) {
                goTo(Number(jump));
                setJump('');
              }
            }}
          />
          Page
        </label>
      )}
    </nav>
  );
};

export default Pagination;
